import { graphql } from '@lib/graphql'
import { execute, getDiscountToken } from '@lib/graphql/execute'
import { CartQuery, ReplicationStrategy } from '@lib/graphql/graphql'
import { QueryConfig } from '@lib/react-query'
import { selectChannel, selectCurrency } from '@redux/globalSlice'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { CHANNEL, eaChannelMap, getChannelId } from 'config'
import { getSession } from 'next-auth/react'
import { useSelector } from 'react-redux'
import { cartsQueryKeys } from '../query-keys'
import { getCartIdFromCookies, saveCartIdToCookie } from '../utils'
import { cartReplicate } from './cart-replicate'
import { updateCart } from './cart-update'
export type Cart = CartQuery['cart']
export type CartLineItem =
  CartQuery['cart']['lineItems']['edges'][number]['node']

const cartQueryDocument = graphql(`
  query Cart($id: ID!) {
    cart(id: $id) {
      ...CartDetail
    }
  }
  fragment CartDetail on Cart {
    id
    lineItemsQuantity
    channel {
      id
      slug
    }
    currency
    customerId
    customerEmail

    discountCodes {
      code
    }
    paymentSession {
      paymentGateway {
        __typename
        id
        name
        ... on StripePaymentGateway {
          publishableKey
        }
      }
      __typename
      ... on StripePaymentSession {
        clientSecret
      }
    }
    shippingAddress {
      ...CartAddress
    }
    billingAddress {
      ...CartAddress
    }

    shippingLines {
      shippingMethod {
        id
        name
      }
      taxBehavior
      taxedPrice {
        gross {
          centAmount
          currencyCode
          fractionDigits
        }
        net {
          centAmount
          currencyCode
          fractionDigits
        }
      }
    }
    availableShippingMethods {
      id
      name
      description
      metadata {
        key
        value
      }

      rate {
        id
        __typename
        ... on RelativeShippingMethodRate {
          rate
        }
        ... on AbsoluteShippingMethodRate {
          price {
            centAmount
            currencyCode
            fractionDigits
          }
        }
      }
    }
    taxedPrice {
      tax {
        centAmount
        currencyCode
        fractionDigits
      }
    }
    total {
      centAmount
      currencyCode
      fractionDigits
    }
    lineItems(first: 50) {
      edges {
        node {
          ...CartLineItem
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  fragment CartLineItem on CartLineItem {
    id
    discountApplications {
      edges {
        node {
          label
          discountedAmount {
            centAmount
            currencyCode
            fractionDigits
          }
        }
      }
    }
    productName
    variantName
    sku
    productSlug
    variant {
      id
      name
      selectedAttributes {
        name
        value
      }
      availability {
        availableForPurchase
        availableQuantity
        stockPolicy
      }
      image {
        src
      }
      product {
        id
        name
        slug
        tags
        vendor
        metadata {
          key
          value
        }
      }
      metadata {
        key
        value
      }
      sku
    }
    unitPrice {
      value {
        centAmount
        currencyCode
        fractionDigits
      }
      discountedPrice {
        value {
          centAmount
          currencyCode
          fractionDigits
        }
      }
    }
    subtotal {
      centAmount
      currencyCode
      fractionDigits
    }
    total {
      centAmount
      currencyCode
      fractionDigits
    }
    taxBehavior
    taxedPrice {
      gross {
        centAmount
        currencyCode
        fractionDigits
      }
      net {
        centAmount
        currencyCode
        fractionDigits
      }
    }
    quantity
    metadata {
      key
      value
    }
  }
  fragment CartAddress on CartAddress {
    id
    email
    firstName
    lastName
    address1
    address2
    city
    postalCode
    state
    company
    phone
    countryCode
  }
`)

export const getCart = async (id?: string) => {
  const cartId = id ? id : getCartIdFromCookies()

  const { cart } = cartId
    ? await execute(cartQueryDocument, { id: cartId })
    : { cart: null }

  return cart
}

export const getCartQueryOptions = ({
  channel,
  currency,
}: {
  currency: string
  channel: CHANNEL
}) => {
  return queryOptions({
    queryKey: cartsQueryKeys.all,
    queryFn: async () => {
      const cart = await getCart()

      var discountToken = await getDiscountToken()
      const channelId = discountToken
        ? eaChannelMap[getChannelId(channel)]
        : getChannelId(channel)

      const session = await getSession()
      if (session?.user?.id !== cart?.customerId) {
        //cart does not belong to user, ignore it
        await updateCart({ customerId: session?.user.id ?? null })
      }

      if (cart.currency !== currency || cart.channel.id !== channelId) {
        const replicatedCart = await cartReplicate({
          cartId: cart.id,
          channelId: getChannelId(channel),
          currency: currency,
          strategy: ReplicationStrategy.SkipUnavailable,
        })

        saveCartIdToCookie(replicatedCart.cartReplicate?.cart.id)
        return replicatedCart.cartReplicate.cart
      }

      return cart
    },
  })
}

export const useCart = (
  queryConfig?: QueryConfig<typeof getCartQueryOptions>,
) => {
  const currency = useSelector(selectCurrency)
  const channel = useSelector(selectChannel)
  return useQuery({
    ...getCartQueryOptions({
      channel,
      currency,
    }),
    ...queryConfig,
  })
}
